/**
 * Planning API Service
 * Based on WP-RETIRE-1 - Retirement Planning Scaffolding
 *
 * Pattern: Follow allocation.ts for BASE_PATH and api import.
 */

import { api } from './api';
import type {
  RetirementPlanningInputs,
  RetirementInputsUpdate,
  RetirementNetWorth,
  RetirementEvent,
  RetirementEventCreate,
  RetirementEventUpdate,
} from '../types/retirement';

const BASE_PATH = '/planning';

/**
 * Planning API service
 *
 * Currently includes retirement planning endpoints.
 * Future planning features (e.g., budgeting, goals) would be added here.
 */
export const planningApi = {
  // ============================================================================
  // Retirement Planning
  // ============================================================================

  /**
   * Get retirement planning inputs for current tenant.
   * Returns 404 if not configured yet.
   */
  getRetirementInputs: () =>
    api.get<RetirementPlanningInputs>(`${BASE_PATH}/retirement`),

  /**
   * Create or update retirement planning inputs (upsert).
   * @param data - Retirement planning inputs
   */
  updateRetirementInputs: (data: RetirementInputsUpdate) =>
    api.put<RetirementPlanningInputs>(`${BASE_PATH}/retirement`, data),

  /**
   * Reset/delete retirement planning inputs.
   */
  deleteRetirementInputs: () =>
    api.delete(`${BASE_PATH}/retirement`),

  /**
   * Get net worth excluding education accounts (529 plans, ESAs).
   * Calculates total from all active accounts with proper liability handling.
   */
  getRetirementNetWorth: () =>
    api.get<RetirementNetWorth>(`${BASE_PATH}/retirement/net-worth`),

  // ============================================================================
  // Retirement Events (WP-RETIRE-7 / WP-RETIRE-8)
  // ============================================================================

  /**
   * List all retirement events for the current tenant.
   * GET /v1/planning/retirement/events
   */
  getRetirementEvents: () =>
    api.get<RetirementEvent[]>(`${BASE_PATH}/retirement/events`),

  /**
   * Create a new retirement event.
   * POST /v1/planning/retirement/events
   */
  createRetirementEvent: (data: RetirementEventCreate) =>
    api.post<RetirementEvent>(`${BASE_PATH}/retirement/events`, data),

  /**
   * Update an existing retirement event.
   * PUT /v1/planning/retirement/events/{id}
   */
  updateRetirementEvent: (id: string, data: RetirementEventUpdate) =>
    api.put<RetirementEvent>(`${BASE_PATH}/retirement/events/${id}`, data),

  /**
   * Delete a retirement event.
   * DELETE /v1/planning/retirement/events/{id}
   */
  deleteRetirementEvent: (id: string) =>
    api.delete(`${BASE_PATH}/retirement/events/${id}`),

};

export default planningApi;
